// POS unified toolbar: tab bar + barcode input + customer/coupon chips.
// Desktop: single row [TabBar | BarcodeInput | CustomerChip | CouponChip]
// Mobile:  row1 [TabBar | CustomerIcon | CouponIcon]  row2 [BarcodeInput]
import { PosTabBar } from "./pos-tab-bar";
import { BarcodeInput } from "./barcode-input";
import { CustomerSelect } from "./customer-select";
import { CouponInput } from "./coupon-input";
import type { Order } from "./use-pos-cart";
import type { ScanResult } from "@/features/inventory/use-scan-resolve";

// Segmented Lẻ/Sỉ switch. Switching to Sỉ re-prices the draft to wholesale.
function OrderTypeToggle({
  orderType,
  onChange,
  disabled,
}: {
  orderType: string;
  onChange: (t: string) => void;
  disabled?: boolean;
}) {
  const isWholesale = orderType === "wholesale";
  return (
    <div className="inline-flex shrink-0 overflow-hidden rounded-md border text-xs font-medium">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("retail")}
        className={`px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
          !isWholesale ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"
        }`}
      >
        Lẻ
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("wholesale")}
        className={`px-2.5 py-1.5 transition-colors disabled:opacity-50 ${
          isWholesale ? "bg-amber-500 text-white" : "bg-background text-muted-foreground"
        }`}
      >
        Sỉ
      </button>
    </div>
  );
}

interface PosToolbarProps {
  // Tab bar
  drafts: Order[];
  activeOrderId?: string;
  onSelectDraft: (orderId: string) => void;
  onCloseDraft: (orderId: string) => void;
  onNewOrder: () => void;
  // Barcode
  onAddVariant: (variantId: string, imageUrl?: string) => void;
  onMultipleResults: (results: Array<{ id: string }>) => void;
  barcodeDisabled?: boolean;
  // Serial-first scan (optional — retail sell context)
  serialResolver?: (q: string) => Promise<ScanResult>;
  onSerialAdd?: (variantId: string, serialId: string) => void;
  onSerialError?: (reason: string) => void;
  // Customer
  selectedCustomerId?: string | null;
  onSelectCustomer: (customer: { id: string } | null) => void;
  // Order type (retail/wholesale)
  orderType: string;
  onSetOrderType: (t: string) => void;
  // Coupon
  order: Order | null;
  isPaid: boolean;
  subtotal: number;
  coupon: { code: string; amountOff: number } | null;
  onApplyCoupon: (result: { code: string; amountOff: number } | null) => void;
  // General
  loading?: boolean;
}

export function PosToolbar({
  drafts,
  activeOrderId,
  onSelectDraft,
  onCloseDraft,
  onNewOrder,
  onAddVariant,
  onMultipleResults,
  barcodeDisabled,
  serialResolver,
  onSerialAdd,
  onSerialError,
  selectedCustomerId,
  onSelectCustomer,
  orderType,
  onSetOrderType,
  order,
  isPaid,
  subtotal,
  coupon,
  onApplyCoupon,
  loading,
}: PosToolbarProps) {
  const showCoupon = !!order && !isPaid;

  return (
    <div className="border-b bg-card shrink-0">
      {/* Desktop: single row */}
      <div className="hidden lg:flex items-center gap-2 px-3 py-2">
        <PosTabBar
          drafts={drafts}
          activeOrderId={activeOrderId}
          onSelect={onSelectDraft}
          onClose={onCloseDraft}
          onNew={onNewOrder}
          loading={loading}
        />
        <div className="flex-1 min-w-0">
          <BarcodeInput
            onAdd={onAddVariant}
            onMultiple={onMultipleResults}
            disabled={barcodeDisabled}
            serialResolver={serialResolver}
            onSerialAdd={onSerialAdd}
            onSerialError={onSerialError}
          />
        </div>
        <OrderTypeToggle orderType={orderType} onChange={onSetOrderType} disabled={isPaid} />
        {/* Desktop: customer selector lives at the top of the payment panel (right column). */}
        {showCoupon && (
          <CouponInput
            subtotal={subtotal}
            applied={coupon}
            onApply={onApplyCoupon}
            mode="chip"
          />
        )}
      </div>

      {/* Mobile: two rows */}
      <div className="flex flex-col gap-2 px-3 py-2 lg:hidden">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <PosTabBar
              drafts={drafts}
              activeOrderId={activeOrderId}
              onSelect={onSelectDraft}
              onClose={onCloseDraft}
              onNew={onNewOrder}
              loading={loading}
            />
          </div>
          <OrderTypeToggle orderType={orderType} onChange={onSetOrderType} disabled={isPaid} />
          <CustomerSelect
            selectedId={selectedCustomerId}
            onSelect={onSelectCustomer}
            mode="chip"
          />
          {showCoupon && (
            <CouponInput
              subtotal={subtotal}
              applied={coupon}
              onApply={onApplyCoupon}
              mode="chip"
            />
          )}
        </div>
        <BarcodeInput
          onAdd={onAddVariant}
          onMultiple={onMultipleResults}
          disabled={barcodeDisabled}
          serialResolver={serialResolver}
          onSerialAdd={onSerialAdd}
          onSerialError={onSerialError}
        />
      </div>
    </div>
  );
}
